import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { buttonStyle } from 'components/buttons/buttonStyle';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput.tsx';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput.tsx';
import { ModalFooter } from 'components/modals/ModalFooter.tsx';
import { ModalHeader } from 'components/modals/ModalHeader.tsx';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { t } from 'utils/getTranslations.ts';
import {
    BulkSelectableExpense,
    ExpenseBulkSelectFilter,
    filterBulkSelectableExpenses,
} from './expensesBulkSelectFilter';

interface Props {
    onClose: () => void;
    onBulkSelect: (ids: number[]) => void;
    onBulkUnselect: (ids: number[]) => void;
    expenses: BulkSelectableExpense[];
}

const defaultValues: ExpenseBulkSelectFilter = {
    employees: [],
    expenseDateFrom: null,
    expenseDateTo: null,
    billable: false,
    nonBillable: false,
};

const ExpensesBulkSelectModal = ({ onClose, onBulkSelect, onBulkUnselect, expenses }: Props) => {
    const isNew = useIsNewDesign();
    const { handleSubmit, control, setValue } = useForm<ExpenseBulkSelectFilter>({
        defaultValues,
    });

    const [
        employeesControlValue,
        expenseDateFromControlValue,
        expenseDateToControlValue,
        billableControlValue,
        nonBillableControlValue,
    ] = useWatch({
        control,
        name: ['employees', 'expenseDateFrom', 'expenseDateTo', 'billable', 'nonBillable'],
    });

    const employeesMap = useMemo(() => {
        const uniqueMap = new Map<number, string>();
        expenses.forEach((exp) => {
            if (exp.employee) uniqueMap.set(exp.employee.id, exp.employee.name);
        });
        return Array.from(uniqueMap.entries()).map(([id, name]) => ({ id, name }));
    }, [expenses]);

    const filterItems = (filter: ExpenseBulkSelectFilter) => filterBulkSelectableExpenses(expenses, filter);

    const onBulkSelectClick = (isToSelect: boolean) => {
        handleSubmit((form) => {
            const result = filterItems(form).map((exp) => exp.id);
            if (isToSelect) {
                onBulkSelect(result);
            } else {
                onBulkUnselect(result);
            }
        })();
        onClose();
    };

    const matchCount = (
        <FormattedMessage
            id="invoices.bulk-select-expenses-modal.match-count"
            values={{
                count: filterItems({
                    employees: employeesControlValue,
                    expenseDateFrom: expenseDateFromControlValue,
                    expenseDateTo: expenseDateToControlValue,
                    billable: billableControlValue,
                    nonBillable: nonBillableControlValue,
                }).length,
            }}
        />
    );
    const unselect = <FormattedMessage id="invoices.bulk-select-expenses-modal.bulk-unselect-button-label" />;

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.bulk-select-expenses-modal.title')}
                description={
                    <FormattedMessage
                        id="invoices.bulk-select-expenses-modal.description"
                        values={{ count: expenses.length }}
                    />
                }
                onCancel={onClose}
            />
            <div className="new:px-6 new:py-6 h-full overflow-y-auto px-8 py-[38px]">
                <div className="flex flex-col gap-4">
                    {/* The new design says this in the header's description. */}
                    <p className="text-body1 new:hidden">
                        <FormattedMessage
                            id="invoices.bulk-select-expenses-modal.description"
                            values={{ count: expenses.length }}
                        />
                    </p>
                    <MultiSelectInput
                        control={control}
                        setValue={setValue}
                        label="forms.select-employee"
                        name="employees"
                        options={employeesMap}
                        getOptionIdentifier="id"
                        getOptionLabel="name"
                    />
                    <div className="flex flex-col gap-4 md:flex-row">
                        <DateInput name="expenseDateFrom" control={control} label="date-from" fullWidth />
                        <DateInput name="expenseDateTo" control={control} label="date-to" fullWidth />
                    </div>
                    <div className="flex gap-6">
                        <CheckboxInput
                            control={control}
                            name="billable"
                            label="invoices.bulk-select-expenses-modal.billable"
                        />
                        <CheckboxInput
                            control={control}
                            name="nonBillable"
                            label="invoices.bulk-select-expenses-modal.non-billable"
                        />
                    </div>
                    {/* The new design says it in the footer, beside the buttons it counts for. */}
                    <p className="text-body1 new:hidden">{matchCount}</p>
                </div>
            </div>
            <ModalFooter
                kit
                onCancel={onClose}
                onSave={() => onBulkSelectClick(true)}
                saveButtonTextId={'invoices.bulk-select-expenses-modal.bulk-select-button-label'}
                // The new design keeps one amber answer: Bulk unselect is outlined, beside Cancel, the count before them.
                note={isNew ? <span role="status">{matchCount}</span> : undefined}
                extraButton={
                    isNew ? undefined : (
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary', size: 'small' })}
                            onClick={() => onBulkSelectClick(false)}
                        >
                            {unselect}
                        </Button>
                    )
                }
                secondaryButton={
                    isNew ? (
                        <Button variant="outline" onClick={() => onBulkSelectClick(false)}>
                            {unselect}
                        </Button>
                    ) : undefined
                }
            />
        </>
    );
};

export default ExpensesBulkSelectModal;
